import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { MdAddCircleOutline, MdCheckCircle, MdSearch } from 'react-icons/md';

const DEFAULT_EXTENSIONS = [
  {
    id: 'ext_random_name',
    name: 'generate_random_name',
    description: 'Generates a random first and last name.',
    example: 'Output: "John Doe"',
    code: `def generate_random_name(input={}):
    first_names = ['John', 'Jane', 'Michael', 'Emily', 'William', 'Emma', 'James', 'Olivia', 'David', 'Sophia']
    last_names = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez']
    
    import random
    first = random.choice(first_names)
    last = random.choice(last_names)
    
    return f"{first} {last}"`
  },
  {
    id: 'ext_random_country',
    name: 'generate_random_country',
    description: 'Generates a random country name.',
    example: 'Output: "Canada"',
    code: `def generate_random_country(input={}):
    countries = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan', 'Brazil', 'India', 'South Africa']
    import random
    return random.choice(countries)`
  },
  {
    id: 'ext_random_id',
    name: 'generate_random_4_digit_id',
    description: 'Generates a random 4-digit ID.',
    example: 'Output: 4829',
    code: `def generate_random_4_digit_id(input={}):
    import random
    return random.randint(1000, 9999)`
  },
  {
    id: 'ext_generate_uuid',
    name: 'generate_uuid',
    description: 'Generates a standard V4 UUID string.',
    example: 'Output: "550e8400-e29b-41d4-a716-446655440000"',
    code: `import uuid
def generate_uuid(input={}):
    return str(uuid.uuid4())`
  },
  {
    id: 'ext_current_timestamp',
    name: 'get_current_timestamp',
    description: 'Returns the current ISO 8601 timestamp.',
    example: 'Output: "2023-11-20T14:30:00.000Z"',
    code: `from datetime import datetime
def get_current_timestamp(input={}):
    return datetime.utcnow().isoformat() + "Z"`
  },
  {
    id: 'ext_format_date',
    name: 'format_date_yyyy_mm_dd',
    description: 'Returns the current date in YYYY-MM-DD format.',
    example: 'Output: "2023-11-20"',
    code: `from datetime import datetime
def format_date_yyyy_mm_dd(input={}):
    return datetime.utcnow().strftime("%Y-%m-%d")`
  },
  {
    id: 'ext_random_email',
    name: 'generate_random_email',
    description: 'Generates a random, unique email address.',
    example: 'Output: "user-8a9d123c@example.com"',
    code: `import random
import string
def generate_random_email(input={}):
    random_str = ''.join(random.choices(string.ascii_lowercase + string.digits, k=8))
    return f"user-{random_str}@example.com"`
  },
  {
    id: 'ext_random_integer',
    name: 'generate_random_integer',
    description: 'Generates a random integer between min and max (inclusive).',
    example: 'Input: 1, 100 => Output: 42',
    code: `import random
def generate_random_integer(input={}):
    min_val = int(input.get('min', 1))
    max_val = int(input.get('max', 100))
    return random.randint(min_val, max_val)`
  },
  {
    id: 'ext_random_string',
    name: 'generate_random_string',
    description: 'Generates a random alphanumeric string of a given length.',
    example: 'Input: 12 => Output: "A1b2C3d4E5f6"',
    code: `import random
import string
def generate_random_string(input={}):
    length = int(input.get('length', 10))
    chars = string.ascii_letters + string.digits
    return ''.join(random.choice(chars) for _ in range(length))`
  },
  {
    id: 'ext_add_days_to_date',
    name: 'add_days_to_date',
    description: 'Adds or subtracts a specific number of days from the current date and returns YYYY-MM-DD.',
    example: 'Input: 5 => Output: "2023-11-25"',
    code: `from datetime import datetime, timedelta
def add_days_to_date(input={}):
    days = int(input.get('days', 0))
    new_date = datetime.utcnow() + timedelta(days=days)
    return new_date.strftime("%Y-%m-%d")`
  }
];

function DefaultExtensions() {
  const [addedIds, setAddedIds] = useState(new Set());
  const [addingId, setAddingId] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetchInstalledExtensions();
  }, []);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  const fetchInstalledExtensions = async () => {
    try {
      const response = await axios.get('/api/custom-functions');
      const functions = Array.isArray(response.data) ? response.data : [];
      
      // Match by name or some unique property if id changes on re-installation
      // Default extensions usually have their actual name as property
      const installedNames = new Set(functions.map(f => f.name));
      
      const matchedIds = new Set();
      DEFAULT_EXTENSIONS.forEach(ext => {
        if (installedNames.has(ext.name)) {
          matchedIds.add(ext.id);
        }
      });

      setAddedIds(matchedIds);
    } catch (err) {
      console.error('Failed to fetch installed extensions:', err);
    }
  };

  const handleAddExtension = async (extension) => {
    setError('');
    setAddingId(extension.id);

    try {
      await axios.post('/api/custom-functions/default', {
        name: extension.name,
        description: extension.description,
        example: extension.example,
        code: extension.code
      });
      
      setAddedIds((prev) => {
        const next = new Set(prev);
        next.add(extension.id);
        return next;
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to add extension');
    } finally {
      setAddingId(null);
    }
  };

  const filteredExtensions = DEFAULT_EXTENSIONS.filter((ext) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;

    return [
      ext.name || '',
      ext.description || '',
      ext.example || '',
      ext.code || '',
      addedIds.has(ext.id) ? 'added installed collection' : 'available'
    ].join(' ').toLowerCase().includes(query);
  });

  return (
    <div>
      <div className="custom-functions-header" style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: '8px' }}>
          <h1 style={{ margin: 0 }}>Default Extensions</h1>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
            Browse and add pre-built utility functions directly to your custom functions collection.
          </p>
        </div>
        {showSearch ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: 'min(460px, 100%)' }}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search extensions, examples, or code"
              style={{ minWidth: '220px' }}
            />
            <button
              type="button"
              className="icon-btn"
              onClick={() => setShowSearch(false)}
              aria-label="Close search"
              title="Close search"
            >
              x
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="icon-btn"
            onClick={() => setShowSearch(true)}
            aria-label="Show search"
            title="Search"
          >
            <MdSearch />
          </button>
        )}
      </div>

      {error && <div className="error" style={{ marginBottom: '15px' }}>{error}</div>}

      {filteredExtensions.length === 0 ? (
        <div className="card">
          <p>No default extensions found for "<strong>{searchTerm}</strong>". Try a different search.</p>
        </div>
      ) : (
      <div className="responsive-grid-4" style={{ gap: '16px' }}>
        {filteredExtensions.map((ext) => {
          const isAdded = addedIds.has(ext.id);
          const isAdding = addingId === ext.id;

          return (
            <div key={ext.id} className="card" style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, minHeight: '120px' }}>
                <h3 style={{ marginTop: 0, fontSize: '18px', wordBreak: 'break-all' }}>{ext.name}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '12px', lineHeight: '1.5' }}>
                  {ext.description}
                </p>
                <code style={{ 
                  fontSize: '11px', display: 'block', marginBottom: '15px', 
                  backgroundColor: 'var(--bg-input)', padding: '8px 10px', 
                  borderRadius: '6px', border: '1px solid var(--border)',
                  color: 'var(--text-main)', overflowX: 'auto'
                }}>
                  {ext.example}
                </code>
              </div>
              
              <button
                type="button"
                className={`btn ${isAdded ? 'btn-secondary' : 'btn-success'}`}
                onClick={() => handleAddExtension(ext)}
                disabled={isAdded || isAdding}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px' }}
              >
                {isAdded ? (
                  <>
                    <MdCheckCircle size={18} /> Added to Collection
                  </>
                ) : isAdding ? (
                  'Adding...'
                ) : (
                  <>
                    <MdAddCircleOutline size={18} /> Add Extension
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

export default DefaultExtensions;
